import React, {
  CSSProperties,
  FC,
  ReactNode,
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import cs from "classnames";
import dayjs, { Dayjs } from "dayjs";
import "./index.less";

type StatisticValue = string | number | Date | Dayjs;
type StatisticClassName = string | string[];

export interface StatisticUIProps
  extends Omit<
    React.HTMLAttributes<HTMLDivElement>,
    "className" | "prefix" | "title"
  > {
  className?: StatisticClassName;
  style?: CSSProperties;
  styleValue?: CSSProperties;
  styleDecimal?: CSSProperties;
  title?: ReactNode;
  value?: StatisticValue;
  precision?: number;
  groupSeparator?: boolean;
  prefix?: ReactNode;
  suffix?: ReactNode;
  extra?: ReactNode;
  countUp?: boolean;
  countFrom?: number;
  countDuration?: number;
  format?: string;
  renderFormat?: (value: StatisticUIProps["value"], formattedValue: string) => ReactNode;
  loading?: boolean;
}

export interface StatisticCountdownProps
  extends Omit<
    React.HTMLAttributes<HTMLDivElement>,
    "className" | "prefix" | "title"
  > {
  className?: StatisticClassName;
  style?: CSSProperties;
  styleValue?: CSSProperties;
  title?: ReactNode;
  value?: StatisticValue;
  format?: string;
  renderFormat?: (valueDiff: number, formattedDiff: string) => ReactNode;
  onFinish?: () => void;
  start?: boolean;
  now?: StatisticValue;
}

const countdownUnits: Array<[string, number]> = [
  ["Y", 1000 * 60 * 60 * 24 * 365],
  ["M", 1000 * 60 * 60 * 24 * 30],
  ["D", 1000 * 60 * 60 * 24],
  ["H", 1000 * 60 * 60],
  ["m", 1000 * 60],
  ["s", 1000],
  ["S", 1],
];

const padStart = (value: string, length: number) => {
  return value.length >= length ? value : `${"0".repeat(length - value.length)}${value}`;
};

const isNumericValue = (value: unknown) => {
  return value !== null && value !== "" && Number.isFinite(Number(value));
};

const toTimestamp = (value: StatisticValue | undefined, format?: string) => {
  if (value === undefined || value === null || value === "") {
    return Date.now();
  }

  if (dayjs.isDayjs(value)) {
    return value.valueOf();
  }

  if (value instanceof Date) {
    return value.getTime();
  }

  if (typeof value === "number") {
    return value;
  }

  const parsedWithFormat = format ? dayjs(value, format) : dayjs(value);
  const parsed = parsedWithFormat.isValid() ? parsedWithFormat : dayjs(value);

  return parsed.isValid() ? parsed.valueOf() : Date.now();
};

const formatCountdown = (millisecond: number, format: string) => {
  let leftMillisecond = Math.max(millisecond, 0);

  return countdownUnits.reduce((current, [name, unit]) => {
    if (!current.includes(name)) {
      return current;
    }

    const value = Math.floor(leftMillisecond / unit);
    leftMillisecond -= value * unit;

    return current.replace(new RegExp(`${name}+`, "g"), (match) => {
      return padStart(String(value), match.length);
    });
  }, format);
};

const formatStatisticValue = ({
  format,
  groupSeparator,
  precision,
  value,
}: Pick<StatisticUIProps, "format" | "groupSeparator" | "precision" | "value">) => {
  let displayValue: StatisticValue | undefined = value;

  if (format) {
    displayValue = dayjs(value).format(format);
  }

  if (typeof precision === "number" && precision >= 0) {
    displayValue = Number(value).toFixed(precision);
  }

  const [rawInt, decimal] = String(displayValue).split(".");
  const int =
    groupSeparator && isNumericValue(value)
      ? Number(rawInt).toLocaleString("en-US")
      : rawInt;

  return {
    decimal,
    int,
  };
};

export const StatisticCountdown = forwardRef<
  HTMLDivElement,
  StatisticCountdownProps
>((props, ref) => {
  const {
    className,
    format = "HH:mm:ss",
    now,
    onFinish,
    renderFormat,
    start = true,
    style,
    styleValue,
    title,
    value,
    ...restProps
  } = props;
  const targetTimestamp = useMemo(() => toTimestamp(value, format), [format, value]);
  const initialNowTimestamp = useMemo(() => toTimestamp(now, format), [format, now]);
  const [valueDiff, setValueDiff] = useState(
    targetTimestamp - initialNowTimestamp
  );
  const finishedRef = useRef(false);

  useEffect(() => {
    finishedRef.current = false;
    setValueDiff(targetTimestamp - initialNowTimestamp);
  }, [initialNowTimestamp, targetTimestamp]);

  useEffect(() => {
    if (!start) {
      return undefined;
    }

    const updateValue = () => {
      const nextDiff = targetTimestamp - Date.now();

      setValueDiff(nextDiff);

      if (nextDiff <= 0 && !finishedRef.current) {
        finishedRef.current = true;
        onFinish?.();
      }
    };

    updateValue();
    const timer = window.setInterval(updateValue, 1000);

    return () => {
      window.clearInterval(timer);
    };
  }, [onFinish, start, targetTimestamp]);

  const formattedDiff = formatCountdown(valueDiff, format);
  const valueNode = renderFormat
    ? renderFormat(valueDiff, formattedDiff)
    : formattedDiff;

  return (
    <div
      {...restProps}
      ref={ref}
      className={cs("statistic-ui", "statistic-ui-countdown", className)}
      style={style}
    >
      {title ? <div className="statistic-ui-title">{title}</div> : null}
      <div className="statistic-ui-content">
        <div className="statistic-ui-value" style={styleValue}>
          {valueNode}
        </div>
      </div>
    </div>
  );
}) as FC<StatisticCountdownProps>;

interface StatisticUIComponent extends FC<StatisticUIProps> {
  Countdown: FC<StatisticCountdownProps>;
}

const StatisticUI = ((props) => {
  const {
    className,
    extra,
    format,
    groupSeparator,
    loading,
    precision,
    prefix,
    renderFormat,
    style,
    styleDecimal,
    styleValue,
    suffix,
    title,
    value,
    ...restProps
  } = props;
  const isNumberValue = isNumericValue(value);
  const { decimal, int } = formatStatisticValue({
    format,
    groupSeparator,
    precision,
    value,
  });
  const formattedValue = renderFormat
    ? renderFormat(value, isNumberValue ? int : String(value))
    : isNumberValue
    ? int
    : String(value);

  return (
    <div
      {...restProps}
      aria-busy={loading || undefined}
      className={cs("statistic-ui", className)}
      style={style}
    >
      {title ? <div className="statistic-ui-title">{title}</div> : null}
      <div className="statistic-ui-content">
        <div className="statistic-ui-value" style={styleValue}>
          {prefix !== undefined && prefix !== null ? (
            <span className="statistic-ui-value-prefix">{prefix}</span>
          ) : null}
          {isNumberValue ? (
            <span className="statistic-ui-value-int">{formattedValue}</span>
          ) : (
            formattedValue
          )}
          {decimal !== undefined || suffix !== undefined ? (
            <span className="statistic-ui-value-decimal" style={styleDecimal}>
              {isNumberValue && decimal !== undefined ? `.${decimal}` : null}
              {suffix !== undefined && suffix !== null ? (
                <span className="statistic-ui-value-suffix">{suffix}</span>
              ) : null}
            </span>
          ) : null}
        </div>
        {extra ? <div className="statistic-ui-extra">{extra}</div> : null}
      </div>
    </div>
  );
}) as StatisticUIComponent;

StatisticUI.Countdown = StatisticCountdown;

export default StatisticUI;
